import { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { classNames } from "helpers";
import ModalIntro from "./ModalIntro";

type SuccessOverlayProps = {
  show: boolean;
  animation: object;
  title: string;
  description: string;
  duration?: number;
  onDismiss: () => void;
};

const SuccessOverlay: React.FC<SuccessOverlayProps> = ({
  show,
  animation,
  title,
  description,
  duration = 3000,
  onDismiss,
}) => {
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setAnimateIn(false);
    setFadeOut(false);
    onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    if (show) {
      setFadeOut(false);
      setVisible(true);

      // Trigger slide-in after mount
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });

      timerRef.current = setTimeout(() => {
        setFadeOut(true);
      }, duration);

      return () => clearTimeout(timerRef.current);
    }
  }, [show, duration]);

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.propertyName === "transform" && fadeOut) {
      handleDismiss();
    }
  };

  if (!visible) return null;

  return ReactDOM.createPortal(
    <div
      className={classNames(
        "fixed inset-0 z-[60] transition-all duration-500 ease-in-out",
        animateIn && !fadeOut
          ? "translate-x-0 opacity-100"
          : fadeOut
            ? "translate-x-full opacity-100"
            : "-translate-x-full opacity-100"
      )}
      onTransitionEnd={handleTransitionEnd}
    >
      <ModalIntro
        animation={animation}
        title={title}
        description={description}
        show={visible}
        animationSize="w-[300px]"
      />
    </div>,
    document.body
  );
};

export default memo(SuccessOverlay);
